import { injectScript } from './script-loader';
import {
  RESOLVE_MODULE,
  CACHE_NAMESPACE,
  ES_MODULE_PASSTHROUGH,
  RAW_VALUE,
  THENABLE_PASSTHROUGH,
} from './types';

const canPassThrough = (value: unknown, mode: number): boolean => {
  if (typeof value !== 'object' || !value) return false;
  if (
    mode & ES_MODULE_PASSTHROUGH &&
    (value as Record<string, unknown>).__esModule
  ) {
    return true;
  }
  return !!(
    mode & THENABLE_PASSTHROUGH &&
    typeof (value as { then?: unknown }).then === 'function'
  );
};

const buildNamespace = (
  value: unknown,
  req: Record<string, unknown>,
): Record<string, unknown> => {
  const ns = Object.create(null);
  (req.r as Function)(ns);
  const getters: Record<string, () => unknown> = {};
  let current = value as Record<string, unknown> | null;
  while (current && current !== Object.prototype) {
    const snapshot = current;
    Object.getOwnPropertyNames(snapshot).forEach(key => {
      if (!getters[key]) getters[key] = () => snapshot[key];
    });
    current = Object.getPrototypeOf(current);
  }
  getters.default = () => value;
  (req.d as Function)(ns, getters);
  return ns;
};

const cacheNamespace = (
  value: unknown,
  mode: number,
  ns: Record<string, unknown>,
  cacheByMode: Map<number, WeakMap<object, unknown>>,
  primitiveCache: Record<string, unknown>,
): unknown => {
  if (typeof value === 'object' && value !== null) {
    let cacheForMode = cacheByMode.get(mode);
    if (!cacheForMode) {
      cacheForMode = new WeakMap();
      cacheByMode.set(mode, cacheForMode);
    }
    if (!cacheForMode.has(value)) cacheForMode.set(value, ns);
    return cacheForMode.get(value);
  }
  const key = `${String(value)}+${mode}`;
  if (!primitiveCache[key]) primitiveCache[key] = ns;
  return primitiveCache[key];
};

export const attachWebpackHelpers = (
  req: Record<string, unknown> & ((id: string | number) => unknown),
  loadedChunks: Record<string, 0 | Promise<void> | undefined>,
  chunkBaseUrl: string,
  chunkMap: Record<string, string>,
) => {
  req.d = (
    exports: Record<string, unknown>,
    getters: Record<string, () => unknown>,
  ) => {
    for (const key in getters) {
      if (Object.hasOwn(getters, key) && !Object.hasOwn(exports, key)) {
        Object.defineProperty(exports, key, {
          enumerable: true,
          get: getters[key],
        });
      }
    }
  };

  req.o = (obj: object, prop: string) => {
    return Object.hasOwn(obj, prop);
  };

  req.r = (exports: Record<string, unknown>) => {
    Object.defineProperty(exports, '__esModule', { value: true });
  };

  req.n = (module: Record<string, unknown>) => {
    const getter =
      module && (module as { __esModule?: boolean }).__esModule
        ? () => module.default
        : () => module;
    (req.d as Function)(getter, { a: getter });
    return getter;
  };

  req.e = (chunkId: string): Promise<void> => {
    if (loadedChunks[chunkId] === 0) return Promise.resolve();
    if (loadedChunks[chunkId]) return loadedChunks[chunkId];

    const filename = chunkMap[String(chunkId)];
    if (!filename) {
      if (Object.keys(chunkMap).length === 0) {
        return Promise.reject(
          new Error(
            `[MF-shim] Chunk "${chunkId}" requested but chunk map is empty — chunk-map.js may have failed to load`,
          ),
        );
      }
      loadedChunks[chunkId] = 0;
      return Promise.resolve();
    }

    const url = `${chunkBaseUrl}${filename}`;
    const promise = injectScript(url).then(
      () => {
        loadedChunks[chunkId] = 0;
      },
      () => {
        delete loadedChunks[chunkId];
        throw new Error(
          `[MF-shim] Failed to load chunk "${chunkId}" from ${url}`,
        );
      },
    );

    loadedChunks[chunkId] = promise;
    return promise;
  };

  const namespaceCacheByMode = new Map<number, WeakMap<object, unknown>>();
  const primitiveNamespaceCache = Object.create(null) as Record<
    string,
    unknown
  >;

  req.t = (rawValue: unknown, mode: number) => {
    let value = rawValue;
    if (mode & RESOLVE_MODULE) value = req(value as string);
    if (mode & RAW_VALUE) return value;

    if (canPassThrough(value, mode)) return value;

    const ns = buildNamespace(value, req);

    return mode & CACHE_NAMESPACE
      ? cacheNamespace(
          value,
          mode,
          ns,
          namespaceCacheByMode,
          primitiveNamespaceCache,
        )
      : ns;
  };

  req.ts = (script: unknown) => script;

  req.nmd = (module: Record<string, unknown>) => {
    module.paths = [];
    if (!module.children) module.children = [];
    return module;
  };

  req.S = {};
  req.I = () => Promise.resolve();
  req.R = undefined;
  req.s = '';
  req.p = chunkBaseUrl;
  req.g = globalThis;
};
